/**
 * LayerDrawer Component
 *
 * Main bottom sheet drawer component for Layer Drawer 2.0.
 * Mobile-first design with smooth animations and gesture support.
 */

import React, { useCallback, useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faFolder, faSearch, faSyncAlt, faCog, faTimes } from '@fortawesome/free-solid-svg-icons';
import './LayerDrawer.scss';
import ContextHelp from '../ContextHelp/ContextHelp';
import { DrawerHandle } from './components/DrawerHandle';
import { ActiveTab } from './components/ActiveTab';
import { BrowseTab } from './components/BrowseTab';
import { SearchTab } from './components/SearchTab';
import { useLayerDrawer } from './LayerDrawerContext';
import { DrawerHeight, calculateDrawerHeight } from './utils/layerDrawerUtils';
import { usePermissions } from '../../context/PermissionContext';
import { useTools } from '../../context/ToolContext';

export const LayerDrawer: React.FC = () => {
  const { drawerHeight, activeTab, setDrawerHeight, setActiveTab, refreshLayers } = useLayerDrawer();
  const [actualHeight, setActualHeight] = useState(0);
  const drawerRef = useRef<HTMLDivElement>(null);
  const { isAdmin, canDo } = usePermissions();
  const { openTool } = useTools();

  /**
   * Handle minimize - hide the drawer completely
   */
  const handleMinimize = useCallback(() => {
    setDrawerHeight(DrawerHeight.HIDDEN);
  }, [setDrawerHeight]);

  /**
   * Update actual height when drawer height state changes
   */
  useEffect(() => {
    const viewportHeight = window.innerHeight;
    const newHeight = calculateDrawerHeight(drawerHeight, viewportHeight);
    setActualHeight(newHeight);
  }, [drawerHeight]);

  /**
   * Handle window resize
   */
  useEffect(() => {
    const handleResize = () => {
      const viewportHeight = window.innerHeight;
      const newHeight = calculateDrawerHeight(drawerHeight, viewportHeight);
      setActualHeight(newHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawerHeight]);

  /**
   * Handle tab change
   */
  const handleTabChange = useCallback((tab: 'active' | 'browse' | 'search') => {
    setActiveTab(tab);

    // Auto-expand drawer to FULL when tab is clicked and drawer is collapsed
    if (drawerHeight === DrawerHeight.COLLAPSED) {
      setDrawerHeight(DrawerHeight.FULL);
    }
  }, [drawerHeight, setActiveTab, setDrawerHeight]);

  /**
   * Handle refresh button
   */
  const handleRefresh = useCallback(async () => {
    await refreshLayers();
  }, [refreshLayers]);

  /**
   * Handle layer admin button (admin only)
   */
  const handleLayerAdmin = useCallback(() => {
    openTool('layerAdmin');
  }, [openTool]);

  /**
   * Render active tab content
   */
  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case 'active':
        return <ActiveTab />;
      case 'browse':
        return <BrowseTab />;
      case 'search':
        return <SearchTab />;
      default:
        return null;
    }
  }, [activeTab]);

  // Don't render anything when hidden
  if (drawerHeight === DrawerHeight.HIDDEN) {
    return null;
  }

  // Hide the drawer entirely when the user lacks layer-read permission.
  // Placed AFTER all hooks (rules-of-hooks) — this is a render-time gate.
  if (!canDo('layers', 'read')) return null;

  return (
    <div
      ref={drawerRef}
      className={`layer-drawer ${drawerHeight === DrawerHeight.COLLAPSED ? 'collapsed' : ''}`}
      style={{
        height: `${actualHeight}px`
      }}
      role="dialog"
      aria-label="Layer controls"
    >
      <DrawerHandle
        currentHeight={drawerHeight}
        onHeightChange={setDrawerHeight}
      />

      <div className="layer-drawer-tabs">
        <button
          className={`layer-drawer-tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => handleTabChange('active')}
          aria-pressed={activeTab === 'active'}
          aria-label="Active layers"
        >
          <span className="tab-icon"><FontAwesomeIcon icon={faBolt} /></span>
          <span className="tab-label">Active</span>
        </button>

        <button
          className={`layer-drawer-tab ${activeTab === 'browse' ? 'active' : ''}`}
          onClick={() => handleTabChange('browse')}
          aria-pressed={activeTab === 'browse'}
          aria-label="Browse layers"
        >
          <span className="tab-icon"><FontAwesomeIcon icon={faFolder} /></span>
          <span className="tab-label">Browse</span>
        </button>

        <button
          className={`layer-drawer-tab ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => handleTabChange('search')}
          aria-pressed={activeTab === 'search'}
          aria-label="Search layers"
        >
          <span className="tab-icon"><FontAwesomeIcon icon={faSearch} /></span>
          <span className="tab-label">Search</span>
        </button>

        <button
          className="layer-drawer-refresh-btn"
          onClick={handleRefresh}
          aria-label="Refresh layers"
          title="Refresh layers"
        >
          <span className="refresh-icon"><FontAwesomeIcon icon={faSyncAlt} /></span>
        </button>

        {/* Admin-only cog icon for Layer Admin Explorer */}
        {isAdmin && (
          <button
            className="layer-drawer-admin-btn"
            onClick={handleLayerAdmin}
            aria-label="Layer Administration"
            title="Layer Administration (Admin Only)"
          >
            <span className="admin-icon"><FontAwesomeIcon icon={faCog} /></span>
          </button>
        )}

        {/* Help icon */}
        <ContextHelp title='Help with Layers' context='layer-drawer-help' />

        {/* Close/minimize button */}
        <button
          className="layer-drawer-close-btn"
          onClick={handleMinimize}
          aria-label="Close layer drawer"
          title="Close"
        >
          <span className="close-icon"><FontAwesomeIcon icon={faTimes} /></span>
        </button>
      </div>

      <div className="layer-drawer-content">
        {renderTabContent()}
      </div>
    </div>
  );
};
